/*
  WOOF quick add for wf-product-card (delegated, works for cards loaded later).
    [data-wf-qa-open]   toggles the card's option panel ("Choose your size")
    [data-wf-qa-close]  closes it
    [data-wf-qa-add]    adds the variant id to the cart
  Adds through /cart/add and announces it with Shopify's standard CartLinesUpdateEvent, the same way
  Horizon's product form does, so Horizon's cart drawer re-renders and opens and the header count updates.
*/
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const routes = (window.Theme && window.Theme.routes) || {};
const addUrl = routes.cart_add_url || '/cart/add';
const cartUrl = routes.cart_url || '/cart';

function closePanel(panel) {
  if (!panel || panel.hidden) return;
  panel.hidden = true;
  const card = panel.closest('[data-wf-card]');
  const opener = card && card.querySelector('[data-wf-qa-open]');
  if (opener) opener.setAttribute('aria-expanded', 'false');
}

function openPanel(card) {
  const panel = card.querySelector('[data-wf-qa-panel]');
  const opener = card.querySelector('[data-wf-qa-open]');
  if (!panel) return;
  document.querySelectorAll('[data-wf-qa-panel]:not([hidden])').forEach((p) => p !== panel && closePanel(p));
  panel.hidden = false;
  if (opener) opener.setAttribute('aria-expanded', 'true');
  const first = panel.querySelector('[data-wf-qa-add]:not([disabled])');
  if (first) first.focus();
}

async function addVariant(button) {
  const id = button.dataset.wfQaAdd;
  if (!id || button.disabled) return;
  const card = button.closest('[data-wf-card]');
  const quantity = 1;

  // Horizon re-renders the cart from these sections in the add response.
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

  const body = new FormData();
  body.append('id', id);
  body.append('quantity', String(quantity));
  if (sectionIds.length) body.append('sections', sectionIds.join(','));

  const deferred = CartLinesUpdateEvent.createPromise();
  (card || document).dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: id, quantity }],
      promise: deferred.promise,
    })
  );

  button.setAttribute('aria-busy', 'true');
  try {
    const response = await fetch(addUrl, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    }).then((r) => r.json());
    const ajaxCart = await fetch(`${cartUrl}.json`).then((r) => r.json());
    const failed = Boolean(response.status);
    if (failed) {
      (card || document).dispatchEvent(
        new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID', detail: { description: response.description } })
      );
    }
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        source: 'wf-product-card',
        itemCount: quantity,
        productId: card ? card.dataset.productId : undefined,
        sections: response.sections,
        didError: failed,
      },
    });
    if (!failed) {
      button.classList.add('is-added');
      setTimeout(() => {
        button.classList.remove('is-added');
        closePanel(button.closest('[data-wf-qa-panel]'));
      }, 900);
    }
  } catch (error) {
    deferred.reject(error);
    console.warn('[WOOF] quick add failed', error);
  } finally {
    button.removeAttribute('aria-busy');
  }
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;
  const open = target.closest('[data-wf-qa-open]');
  if (open) {
    const card = open.closest('[data-wf-card]');
    const panel = card && card.querySelector('[data-wf-qa-panel]');
    if (panel && !panel.hidden) closePanel(panel);
    else if (card) openPanel(card);
    return;
  }
  const close = target.closest('[data-wf-qa-close]');
  if (close) {
    const panel = close.closest('[data-wf-qa-panel]');
    closePanel(panel);
    const opener = panel && panel.closest('[data-wf-card]').querySelector('[data-wf-qa-open]');
    if (opener) opener.focus();
    return;
  }
  const add = target.closest('[data-wf-qa-add]');
  if (add) {
    event.preventDefault();
    addVariant(add);
    return;
  }
  // Click outside an open panel closes it
  if (!target.closest('[data-wf-qa-panel]')) {
    document.querySelectorAll('[data-wf-qa-panel]:not([hidden])').forEach(closePanel);
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const panel = document.querySelector('[data-wf-qa-panel]:not([hidden])');
  if (!panel) return;
  closePanel(panel);
  const opener = panel.closest('[data-wf-card]').querySelector('[data-wf-qa-open]');
  if (opener) opener.focus();
});
